/* eslint-disable */
'use client'

import { useEffect, useRef, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { useDebouncedCallback } from 'use-debounce'
import { useCompletion } from 'ai/react'
import { toast } from 'sonner'
import va from '@vercel/analytics'
import { getPrevText } from '@utils/editor'
import CloseButton from '@components/CloseButton'
import FormCard from '@containers/dao/components/FormCard'
import { TiptapExtensions } from './extensions'
import { TiptapEditorProps } from './props'
import { EditorBubbleMenu } from './components/bubble-menu'
import { ImageResizer } from './components/image-resizer'
import { useTranslation } from 'react-i18next'

type DocContent = {
  type: string
  content: {
    type: string
    content: {
      type: string
      text: string
    }[]
  }[]
}

type EditorProps = {
  onClose?: () => void
  onContentSet?: (content: JSON) => void
  defaultEditorContent?: JSON
  disabled?: boolean
}

export default function Editor({
  onClose,
  onContentSet,
  defaultEditorContent,
  disabled,
}: EditorProps) {
  const { t } = useTranslation()
  const [content, setContent] = useState(defaultEditorContent)
  const [hydrated, setHydrated] = useState(false)

  const debouncedUpdates = useDebouncedCallback(async ({ editor }) => {
    const json = editor.getJSON()
    setContent(json)
    onContentSet(json)
  }, 750)

  const editor = useEditor({
    extensions: TiptapExtensions(disabled),
    editorProps: TiptapEditorProps,
    onUpdate: (e) => {
      const selection = e.editor.state.selection
      const lastTwo = getPrevText(e.editor, {
        chars: 2,
      })
      if (lastTwo === '++' && !isLoading) {
        e.editor.commands.deleteRange({
          from: selection.from - 2,
          to: selection.from,
        })
        complete(
          getPrevText(e.editor, {
            chars: 5000,
          }),
        )
        va.track(t('editor.general.shortCutUsed'))
      } else {
        debouncedUpdates(e)
      }
    },
    autofocus: disabled ? false : t('editor.general.end'),
  })

  const { complete, completion, isLoading, stop } = useCompletion({
    id: 'novel',
    api: '/api/generate',
    onFinish: (_prompt, completion) => {
      editor?.commands.setTextSelection({
        from: editor.state.selection.from - completion.length,
        to: editor.state.selection.from,
      })
    },
    onError: (err) => {
      toast.error(err.message)
      if (err.message === t('editor.commands.error.requestLimit')) {
        va.track(t('editor.commands.error.rateLimit'))
      }
    },
  })

  const prev = useRef('')

  // Insert chunks of the generated text
  useEffect(() => {
    const diff = completion.slice(prev.current.length)
    prev.current = completion
    editor?.commands.insertContent(diff)
  }, [isLoading, editor, completion])

  useEffect(() => {
    // if user presses escape or cmd + z and it's loading,
    // stop the request, delete the completion, and insert back the "++"
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || (e.metaKey && e.key === 'z')) {
        stop()
        if (e.key === 'Escape') {
          editor?.commands.deleteRange({
            from: editor.state.selection.from - completion.length,
            to: editor.state.selection.from,
          })
        }
        editor?.commands.insertContent('++')
      }
    }
    const mousedownHandler = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      stop()
      if (window.confirm(t('editor.general.confirm'))) {
        complete(editor?.getText() || '')
      }
    }
    if (isLoading) {
      document.addEventListener('keydown', onKeyDown)
      window.addEventListener('mousedown', mousedownHandler)
    } else {
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('mousedown', mousedownHandler)
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('mousedown', mousedownHandler)
    }
  }, [stop, isLoading, editor, complete, completion.length])

  // Hydrate the editor with the content from localStorage.
  useEffect(() => {
    if (editor && content && !hydrated) {
      const docContent = content as unknown as DocContent
      // Remove empty contents
      const filteredContent =
        docContent.content?.length > 0
          ? docContent.content.filter((c) => c?.content)
          : []

      editor.commands.setContent({
        ...docContent,
        content: filteredContent,
      })
      editor.setEditable(!disabled)
      onContentSet(docContent as unknown as JSON)
      setHydrated(true)
    }
  }, [editor, content, hydrated, onContentSet, disabled])

  return (
    <FormCard tabIndex={disabled ? -1 : undefined}>
      <div
        tabIndex={disabled ? -1 : undefined}
        className={`relative ${disabled ? '' : 'min-h-[500px]'} w-full max-w-screen-lg p-12 px-8`}
        onClick={() => {
          editor?.chain().focus().run()
        }}
        style={{
          color: 'white',
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        {onClose && (
          <CloseButton
            onClick={onClose}
            className="absolute right-5 top-5 mb-5"
          />
        )}
        {editor && <EditorBubbleMenu editor={editor} />}
        {editor?.isActive('image') && <ImageResizer editor={editor} />}
        <EditorContent editor={editor} />
      </div>
    </FormCard>
  )
}
